<template>
  <Empathize
    :animation="empathizeAnimation"
    class="x-z-40 x-bg-neutral-0 desktop:x-w-full"
    :events-to-close-empathize="isDesktopOrGreater ? desktopCloseEvents : mobileCloseEvents"
  >
    <component
      :is="isDesktopOrGreater ? 'div' : 'BaseScroll'"
      class="x-h-full desktop:x-ml-16"
      :class="{ 'x-bg-neutral-0': showEmpathize }"
    >
      <div
        v-if="showEmpathize || showIdentifierResults"
        class="x-layout-item x-pb-16 desktop:x-block desktop:x-pl-16 desktop:x-pt-16"
        :class="{ 'x-mb-40 x-border-b x-border-neutral-10': !x.query.search && isTabletOrLess }"
      >
        <BaseKeyboardNavigation
          class="x-flex x-flex-col desktop:x-grid desktop:x-grid-cols-12 desktop:x-items-start desktop:x-gap-24"
          :navigation-hijacker="navigationHijacker"
        >
          <PredictiveIdentifierResults
            v-if="showIdentifierResults"
            :animation="suggestionsAnimation"
          />

          <div
            v-else-if="showEmpathize"
            class="x-flex x-flex-col x-gap-8 x-pl-8 desktop:x-col-span-5 desktop:x-pl-0"
            :class="x.query.searchBox ? 'desktop:x-gap-4' : 'desktop:x-gap-16'"
          >
            <div v-if="showHistoryQueries" class="x-flex x-flex-col x-gap-4">
              <div v-if="!x.query.searchBox" class="x-flex x-items-center">
                <h1 class="x-title4 x-title4-sm x-flex-1 x-uppercase x-text-neutral-50">
                  {{ $t('historyQueries.title') }}
                </h1>
                <ClearHistoryQueries class="x-button-neutral x-button-sm x-button-tight x-px-8">
                  <TrashIcon v-if="isTabletOrLess" class="x-icon-lg" />
                  <span v-else>{{ $t('historyQueries.clear') }}</span>
                </ClearHistoryQueries>
              </div>

              <HistoryQueries
                :animation="suggestionsAnimation"
                :max-items-to-render="x.query.searchBox ? 2 : 4"
                suggestion-item-class="x-w-full"
                class="-x-mr-2 x-flex x-flex-col x-gap-8 desktop:x-gap-4"
              >
                <template #suggestion="{ suggestion }">
                  <HistoryQuery
                    class="x-suggestion-group-lg x-text-lead-75 desktop:x-suggestion-group-md hover:x-no-underline"
                    :suggestion="suggestion"
                    suggestion-class="x-suggestion x-suggestion-lg desktop:x-suggestion-md"
                  >
                    <template #default="{ query }">
                      <HistoryIcon class="x-icon-lg desktop:x-icon-md" />
                      <div class="x-group x-flex x-flex-col x-gap-2">
                        <Highlight
                          :text="suggestion.query"
                          :highlight="query"
                          class="group-hover:x-underline"
                        />
                        <HistoryQueryFilters
                          class="x-w-192 desktop:x-w-128"
                          :filters-list="suggestion.selectedFilters"
                        />
                      </div>
                    </template>

                    <template #remove-button-content>
                      <span
                        :aria-label="
                          $t('historyQueries.removeLabel', { suggestion: suggestion.query })
                        "
                      >
                        <CrossTinyIcon class="x-icon-lg desktop:x-icon-md" />
                      </span>
                    </template>
                  </HistoryQuery>
                </template>
              </HistoryQueries>
            </div>

            <PredictiveQuerySuggestions
              v-if="showQuerySuggestions"
              :animation="suggestionsAnimation"
            />

            <PredictiveNextQueries v-if="showNextQueries" :animation="suggestionsAnimation" />

            <PredictivePopularSearches
              v-if="showPopularSearches"
              :animation="suggestionsAnimation"
            />
          </div>

          <SlidingRecommendations
            v-if="isDesktopOrGreater && !x.query.searchBox"
            class="x-col-span-7"
          />
        </BaseKeyboardNavigation>
      </div>
      <SlidingRecommendations v-if="isTabletOrLess && !x.query.searchBox" />
    </component>
  </Empathize>
</template>

<script setup lang="ts">
import type { XEventsTypes } from '@empathyco/x-components'
import {
  AnimateScale,
  BaseKeyboardNavigation,
  CrossTinyIcon,
  Highlight,
  HistoryIcon,
  StaggeredFadeAndSlide,
  TrashIcon,
  use$x,
} from '@empathyco/x-components'
import { Empathize } from '@empathyco/x-components/empathize'
import {
  ClearHistoryQueries,
  HistoryQueries,
  HistoryQuery,
} from '@empathyco/x-components/history-queries'
import { useDevice } from '../../composables/use-device.composable'
import { usePredictiveHelpers } from '../../composables/use-predictive-helpers.composable'
import HistoryQueryFilters from '../history-query-filters.vue'
import PredictiveIdentifierResults from './predictive-identifier-results.vue'
import PredictiveNextQueries from './predictive-next-queries.vue'
import PredictivePopularSearches from './predictive-popular-searches.vue'
import PredictiveQuerySuggestions from './predictive-query-suggestions.vue'
import SlidingRecommendations from './sliding-recommendations.vue'

const empathizeAnimation = AnimateScale
const suggestionsAnimation = StaggeredFadeAndSlide as any
const { isDesktopOrGreater, isTabletOrLess } = useDevice()

const {
  navigationHijacker,
  showIdentifierResults,
  showHistoryQueries,
  showQuerySuggestions,
  showNextQueries,
  showPopularSearches,
  showEmpathize,
} = usePredictiveHelpers()

const mobileCloseEvents: Array<keyof XEventsTypes> = [
  'UserClosedEmpathize',
  'UserSelectedASuggestion',
  'UserPressedEnterKey',
]
const desktopCloseEvents: Array<keyof XEventsTypes> = [...mobileCloseEvents, 'UserBlurredSearchBox']

const x = use$x()
</script>
<style lang="scss">
.x-result-link:focus-visible > * {
  outline: -webkit-focus-ring-color auto 1px;
}
</style>
